import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, CheckCircle2, Circle, Clock, ShieldAlert } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Card, Badge } from "../ui/Card";
import Button from "../ui/Button";
import Input from "../ui/Input";

// Where each checklist step is completed.
const STEP_LINKS = {
  profile: "/business/settings",
  category: "/business/settings",
  services: "/business/services",
  availability: "/business/staff",
};

const CHECK_LABEL = {
  phone: "Phone number",
  identity: "Your identity",
  business_registration: "Business registration",
  professional_license: "Professional licence",
  payout: "Payout account",
};

const CHECK_STATUS = {
  verified: ["Verified", "accent"],
  pending: ["Being checked", "warning"],
  rejected: ["Not accepted — send again", "warning"],
  failed: ["Too many wrong codes", "warning"],
  not_submitted: ["Not sent yet", "default"],
  not_required: ["Not needed", "default"],
};

const REFERENCE_LABEL = {
  identity: "National ID or passport number",
  business_registration: "Registration number / TIN",
  professional_license: "Licence number",
};

const errorOf = (err, fallback) => {
  const data = err?.response?.data;
  return data?.errors?.checklist?.join(" ") || Object.values(data?.errors || {})[0]?.[0] || data?.message || fallback;
};

function PhoneStep({ onDone }) {
  const [sentTo, setSentTo] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const run = async (request, fallback) => {
    setBusy(true);
    setMessage(null);
    try {
      return await request();
    } catch (err) {
      setMessage(errorOf(err, fallback));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    const res = await run(() => api.post("/account/phone/verification"), "Could not send the code.");
    if (res?.data?.verified) onDone();
    else if (res) setSentTo(res.data.sent_to);
  };

  const confirm = async (e) => {
    e.preventDefault();
    const res = await run(() => api.post("/account/phone/verification/confirm", { code }), "Invalid or expired code.");
    if (res) onDone();
  };

  return (
    <div className="mt-2">
      {sentTo ? (
        <form onSubmit={confirm} className="flex flex-wrap items-center gap-2">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            placeholder={`Code sent to ${sentTo}`}
            aria-label="Verification code"
            className="w-56"
          />
          <Button size="sm" type="submit" disabled={busy || code.length < 6}>Verify</Button>
          <button type="button" onClick={send} disabled={busy} className="text-xs text-primary underline">Send again</button>
        </form>
      ) : (
        <Button size="sm" variant="outline" onClick={send} disabled={busy}>Text me a code</Button>
      )}
      {message && <p className="mt-1 text-xs text-destructive">{message}</p>}
    </div>
  );
}

function VerificationForm({ type, check, onDone }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  // The payout number is usually the one they signed up with.
  const [form, setForm] = useState({ reference_number: "", phone: user?.phone || "", provider: "mtn", account_name: user?.full_name || "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (["verified", "pending"].includes(check.status)) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (type === "payout") {
        await api.post("/owner/verifications/payout", { phone: form.phone, provider: form.provider, account_name: form.account_name });
      } else {
        const body = new FormData();
        body.append("reference_number", form.reference_number);
        body.append("document", file);
        await api.post(`/owner/verifications/${type}`, body);
      }
      setOpen(false);
      onDone();
    } catch (err) {
      setError(errorOf(err, "Could not send this. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="text-xs font-semibold text-primary underline">
        {check.status === "rejected" ? "Send again" : "Send now"}
      </button>
    );
  }

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <form onSubmit={submit} className="mt-2 w-full space-y-2 rounded-xl bg-muted/50 p-3">
      {type === "payout" ? (
        <>
          <Input value={form.account_name} onChange={set("account_name")} placeholder="Name on the mobile money account" required />
          <div className="flex gap-2">
            <select value={form.provider} onChange={set("provider")} aria-label="Mobile money network" className="h-11 rounded-xl border border-input bg-white px-3 text-sm">
              <option value="mtn">MTN</option>
              <option value="airtel">Airtel</option>
            </select>
            <Input value={form.phone} onChange={set("phone")} placeholder="Mobile money number" required />
          </div>
        </>
      ) : (
        <>
          <Input value={form.reference_number} onChange={set("reference_number")} placeholder={REFERENCE_LABEL[type]} required />
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            aria-label="Photo or PDF of the document"
            required
            className="block w-full text-xs"
          />
          <p className="text-xs text-muted-foreground">A clear photo or PDF, up to 8 MB. Only the SHUWA team can see it.</p>
        </>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex gap-2">
        <Button size="sm" type="submit" disabled={busy}>{busy ? "Sending…" : "Send for checking"}</Button>
        <Button size="sm" type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </form>
  );
}

// Tips for a live shop that help it get booked. The rest of the checks are
// for the optional badge.
const TIP_KEYS = ["services", "availability", "profile", "category"];

/**
 * Where the owner's shop stands. A new shop is live from the start, so for
 * most owners this is a short note with tips. The Verified badge is an
 * optional extra, folded away until they want it. A shop from before shops
 * went live at once gets the "Go live" button.
 */
export default function GoLive({ onChanged }) {
  const [state, setState] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = () => api.get("/owner/onboarding").then(({ data }) => setState(data)).catch(() => setState(null));

  useEffect(() => {
    load();
  }, []);

  if (!state) return null;

  const { status, checklist, verifications } = state;
  const editable = ["draft", "needs_info"].includes(status);
  const checks = Object.values(verifications).filter((c) => c.type !== "phone" && c.status !== "not_required");
  const allVerified = state.is_verified && state.withdrawals_enabled;
  const tips = checklist.filter((item) => TIP_KEYS.includes(item.key) && !item.done);

  // A live shop with nothing left to suggest has nothing to show here.
  if (status === "approved" && allVerified && tips.length === 0) return null;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/owner/onboarding/submit", { accept_terms: acceptTerms || state.terms_accepted });
      setState(data);
      // The page around this card shows the shop's status too.
      onChanged?.();
    } catch (err) {
      setError(errorOf(err, "Could not publish your shop. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const tipList = tips.length > 0 && (
    <ul className="mt-3 space-y-2">
      {tips.map((item) => (
        <li key={item.key} className="flex items-start gap-2 text-sm">
          <Circle className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />
          <span className="text-slate-800">
            {item.label}
            {item.hint && <span className="block text-xs text-muted-foreground">{item.hint}</span>}
            {STEP_LINKS[item.key] && (
              <Link to={STEP_LINKS[item.key]} className="text-xs font-semibold text-primary underline">Do this now</Link>
            )}
          </span>
        </li>
      ))}
    </ul>
  );

  return (
    <Card className="mb-6 p-5">
      {status === "approved" && (
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <div className="min-w-0 flex-1">
            <h2 className="font-montserrat text-base font-bold text-slate-900">Your shop is live</h2>
            <p className="text-sm text-muted-foreground">
              Customers can find and book you now.{tips.length > 0 ? " A few things that help you get more bookings:" : ""}
            </p>
            {tipList}
          </div>
        </div>
      )}
      {status === "draft" && (
        <>
          <h2 className="font-montserrat text-lg font-bold text-slate-900">Publish your shop</h2>
          <p className="text-sm text-muted-foreground">Customers can't see your shop yet. Publish it and it goes live straight away.</p>
          {tipList}
        </>
      )}
      {status === "needs_info" && (
        <div className="flex items-start gap-3">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <h2 className="font-montserrat text-lg font-bold text-slate-900">We need a little more from you</h2>
            <p className="text-sm text-slate-700">{state.review_note}</p>
            <p className="text-sm text-muted-foreground">Update your shop, then send it again below.</p>
          </div>
        </div>
      )}
      {status === "pending" && (
        <div className="flex items-start gap-3">
          <Clock className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div>
            <h2 className="font-montserrat text-lg font-bold text-slate-900">Your shop is being reviewed</h2>
            <p className="text-sm text-muted-foreground">The SHUWA team is checking a few details. We'll notify you as soon as you're live — there's nothing else you need to do right now.</p>
          </div>
        </div>
      )}
      {["suspended", "rejected"].includes(status) && (
        <div className="flex items-start gap-3">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
          <div>
            <h2 className="font-montserrat text-lg font-bold text-slate-900">
              {status === "suspended" ? "Your shop has been suspended" : "Your shop was not accepted"}
            </h2>
            <p className="text-sm text-slate-700">{state.review_note}</p>
            <p className="text-sm text-muted-foreground">
              Your shop is hidden and can't take new bookings. Existing appointments stay on record. Contact support@shuwa.app if you think this is a mistake.
            </p>
          </div>
        </div>
      )}

      {editable && (
        <div className="mt-4">
          {!state.terms_accepted && (
            <label className="mb-3 flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} className="mt-1" />
              <span>
                I accept the SHUWA{" "}
                <a href="/legal/terms" target="_blank" rel="noreferrer" className="text-primary underline">Terms and Conditions</a> and{" "}
                <a href="/legal/business-guidelines" target="_blank" rel="noreferrer" className="text-primary underline">business guidelines</a>.
              </span>
            </label>
          )}
          {error && <p className="mb-2 text-sm text-destructive">{error}</p>}
          <Button onClick={submit} disabled={busy || !state.can_submit || !(acceptTerms || state.terms_accepted)}>
            {busy ? "Sending…" : status === "needs_info" ? "Send again" : "Go live"}
          </Button>
        </div>
      )}

      {/* The badge is an extra: folded away, never in the way of taking bookings. */}
      {!["suspended", "rejected"].includes(status) && !allVerified && (
        <details className="group mt-4 border-t border-border pt-3">
          <summary className="flex cursor-pointer list-none items-center gap-1.5 text-sm font-medium text-slate-700">
            <BadgeCheck className="h-4 w-4 text-primary" /> Verified badge &amp; withdrawals
            <span className="text-xs font-normal text-muted-foreground">(optional)</span>
            <span className="ml-auto text-xs text-primary group-open:hidden">Show</span>
          </summary>
          <p className="mt-2 text-xs text-muted-foreground">
            Not needed to take bookings. The badge shows customers the SHUWA team has checked you. Online payments and
            deposits go into your SHUWA wallet from day one; withdrawals open once your payout account is checked. The SHUWA team reviews each item.
          </p>
          <ul className="mt-2 space-y-2">
            {verifications.phone && verifications.phone.status !== "verified" && (
              <li className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-slate-800">{CHECK_LABEL.phone}</span>
                <Badge variant={CHECK_STATUS[verifications.phone.status]?.[1] || "default"}>
                  {CHECK_STATUS[verifications.phone.status]?.[0] || verifications.phone.status}
                </Badge>
                {verifications.phone.status !== "failed" && <PhoneStep onDone={load} />}
              </li>
            )}
            {checks.map((check) => {
              const [label, variant] = CHECK_STATUS[check.status] || [check.status, "default"];
              return (
                <li key={check.type} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-slate-800">{CHECK_LABEL[check.type]}</span>
                  <Badge variant={variant}>{label}</Badge>
                  {check.type === "payout" && check.details?.phone && (
                    <span className="text-xs text-muted-foreground">{check.details.phone}</span>
                  )}
                  {check.rejection_reason && <span className="text-xs text-amber-700">{check.rejection_reason}</span>}
                  <VerificationForm type={check.type} check={check} onDone={load} />
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </Card>
  );
}
